import { useEffect, useState, useCallback } from "react";
import { CheckCircle2, Circle, X, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { ProfileDialog } from "./ProfileDialogs";

const STEPS = [
  ["details", "Confirm my details", "Check your name, division and position are right."],
  ["mobile", "Add a mobile number", "Used for SMS security codes when you sign in."],
  ["welcome", "Read the welcome message", "A short note from your organisation."],
  ["tour", "Tour the dashboard", "A quick look at where everything lives."],
  ["policies", "Review key policies", "Read the policies everyone needs to know."],
];
const TOUR = [
  ["Your sidebar", "Everything you can use is in the left menu. You only see the areas your role gives you. Collapse it with the gold arrow."],
  ["Your dashboard", "Your home screen shows today's date, your Daily Message and your Key Dates. Use Arrange my dashboard to move things around."],
  ["Key Dates", "Orange means due in 2 days, green means due today, red means overdue. Tick items off when they're done."],
  ["Your profile", "Click your name at the bottom left to update your profile or change your password."],
];

function defaultWelcome(org, name) {
  return `Welcome to ${org}, ${name}.\n\nBOS HQ is where we keep our work, records and key dates in one place. Take a few minutes to finish this checklist, and reach out to your team leader if you have any questions.`;
}

function TourDialog({ onDone, onClose }) {
  const [i, setI] = useState(0);
  const last = i === TOUR.length - 1;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-popover" data-testid="onboarding-tour-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">{TOUR[i][0]}</DialogTitle><DialogDescription className="text-xs">Step {i + 1} of {TOUR.length}</DialogDescription></DialogHeader>
        <p className="text-sm text-slate-300 leading-relaxed">{TOUR[i][1]}</p>
        <DialogFooter>
          {i > 0 && <Button variant="ghost" onClick={() => setI(i - 1)} data-testid="tour-back-btn">Back</Button>}
          <Button onClick={() => (last ? onDone() : setI(i + 1))} data-testid="tour-next-btn">{last ? "Finish tour" : "Next"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InfoDialog({ title, testid, children, action, onAction, onClose }) {
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-popover" data-testid={testid}>
        <DialogHeader><DialogTitle className="font-display text-xl">{title}</DialogTitle><DialogDescription className="sr-only">{title}</DialogDescription></DialogHeader>
        {children}
        <DialogFooter><Button onClick={onAction} data-testid={`${testid}-confirm`}>{action}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StepList({ ob, open }) {
  return (
    <div className="space-y-1.5">
      {STEPS.map(([k, label, hint]) => {
        const done = ob.steps[k];
        return (
          <button key={k} onClick={() => !done && open(k)} disabled={done} data-testid={`onboarding-step-${k}`} data-done={done}
            className={`w-full flex items-start gap-3 rounded-md border px-3 py-2.5 text-left transition-colors ${done ? "border-white/5 opacity-60" : "border-[#C2A462]/25 hover:border-[#C2A462]/70 hover:bg-[#C2A462]/[0.05]"}`}>
            {done ? <CheckCircle2 className="h-4 w-4 mt-0.5 text-[#C2A462]" /> : <Circle className="h-4 w-4 mt-0.5 text-slate-500" />}
            <span><span className={`block text-sm ${done ? "line-through text-slate-400" : "text-slate-200"}`}>{label}</span><span className="block text-xs text-muted-foreground">{hint}</span></span>
          </button>
        );
      })}
    </div>
  );
}

function Bar({ ob }) {
  return <div className="h-1.5 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-[#C2A462] transition-[width] duration-500" style={{ width: `${(ob.done / ob.total) * 100}%` }} /></div>;
}

export function OnboardingChecklist() {
  const { user } = useAuth();
  const [ob, setOb] = useState(null);
  const [step, setStep] = useState(null);
  const [popup, setPopup] = useState(false);
  const load = useCallback(() => api.get("/onboarding").then(({ data }) => { setOb(data); if (data.eligible && !data.popup_seen && data.done < data.total) setPopup(true); }).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  if (!ob?.eligible || (ob.done === ob.total && !step)) return null;
  const mark = async (k) => {
    try { const { data } = await api.post(`/onboarding/step/${k}`); setStep(null); setOb({ ...ob, ...data }); if (data.done === data.total) toast.success("Checklist complete. Welcome aboard!"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const closePopup = () => { setPopup(false); api.post("/onboarding/popup-seen").catch(() => {}); };
  const dismiss = () => { api.post("/onboarding/dismiss").catch(() => {}); setOb({ ...ob, dismissed: true }); };
  const open = (k) => { setPopup(false); if (popup) api.post("/onboarding/popup-seen").catch(() => {}); setStep(k); };
  const first = user.name.split(" ")[0];
  return (
    <>
      {!ob.dismissed && (
        <div className="md:col-span-6 rounded-md border border-[#C2A462]/40 bg-card p-5" data-testid="onboarding-card">
          <div className="flex items-start justify-between gap-4 mb-3">
            <div><div className="overline">Getting started</div><div className="text-sm text-slate-300 mt-1">{ob.done} of {ob.total} done</div></div>
            <button onClick={dismiss} className="text-slate-500 hover:text-[#C2A462]" title="Hide checklist" data-testid="onboarding-dismiss-btn"><X className="h-4 w-4" /></button>
          </div>
          <div className="mb-4"><Bar ob={ob} /></div>
          <div className="grid md:grid-cols-2 gap-x-4"><StepList ob={ob} open={open} /></div>
        </div>
      )}
      {popup && (
        <Dialog open onOpenChange={closePopup}>
          <DialogContent className="max-w-lg bg-popover" data-testid="onboarding-popup">
            <DialogHeader><DialogTitle className="font-display text-2xl">Welcome to {ob.org}, {first}</DialogTitle>
              <DialogDescription className="text-xs">Five quick steps to get you set up. You can come back to these any time from your dashboard.</DialogDescription></DialogHeader>
            <Bar ob={ob} />
            <StepList ob={ob} open={open} />
            <DialogFooter><Button variant="ghost" onClick={closePopup} data-testid="onboarding-popup-later">I'll do this later</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {step === "details" && <ProfileDialog onClose={() => { setStep(null); load(); }} />}
      {step === "mobile" && <ProfileDialog onClose={() => { setStep(null); load(); }} />}
      {step === "tour" && <TourDialog onClose={() => setStep(null)} onDone={() => mark("tour")} />}
      {step === "welcome" && (
        <InfoDialog title="A welcome from your team" testid="onboarding-welcome-dialog" action="I've read this" onAction={() => mark("welcome")} onClose={() => setStep(null)}>
          <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap">{ob.welcome_message || defaultWelcome(ob.org, first)}</p>
        </InfoDialog>
      )}
      {step === "policies" && (
        <InfoDialog title="Key policies" testid="onboarding-policies-dialog" action="I've reviewed these" onAction={() => mark("policies")} onClose={() => setStep(null)}>
          {ob.policies.length ? (
            <ul className="space-y-2">{ob.policies.map((p, i) => (
              <li key={i} className="flex items-center justify-between gap-3 rounded-md border px-3 py-2 text-sm" data-testid={`onboarding-policy-${i}`}>
                <span>{p.title}{p.version && <span className="text-xs text-muted-foreground"> · v{p.version}</span>}</span>
                {p.url && <a href={p.url} target="_blank" rel="noreferrer" className="text-[#C2A462] text-xs inline-flex items-center gap-1">Open <ExternalLink className="h-3 w-3" /></a>}
              </li>))}
            </ul>
          ) : <p className="text-sm text-muted-foreground">Your organisation hasn't added policies yet. Your team leader will take you through them.</p>}
        </InfoDialog>
      )}
    </>
  );
}
